import ImageWithFallback from '@/components/common/image-with-fallback'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import { Home, SettingsIcon, UserIcon } from 'lucide-react'
import Link from 'next/link'
import useSWRImmutable from 'swr/immutable'
import CommunityDisplay from './community-display'
import MobileDropdownNav from './mobile-dropdown-nav'
import DiscordBotCTA from './sidebar-items/discord-bot-cta'

const itemsSection = [
  {
    title: 'Home',
    urlSnippet: 'racing',
    icon: <Home />,
  },
  {
    title: 'Members',
    urlSnippet: 'members',
    icon: <UserIcon />,
  },
]

export default function CommunityContent({ params, pathname, session }) {
  const { data: community, isLoading } = useSWRImmutable(
    {
      url: `community/${params.communityId}`,
      searchParams: {
        select: {
          id: true,
          name: true,
          logoImgUrl: true,
          isShowcase: true,
          championships: {
            where: {
              deleteTs: null,
            },
            select: {
              id: true,
              name: true,
              headerImgUrl: true,
            },
          },
        },
      },
    },
    apiFetch,
  )

  return (
    <>
      <div className="tablet:hidden">
        <MobileDropdownNav
          options={
            community?.isShowcase
              ? itemsSection.filter((i) => i.urlSnippet !== 'members')
              : itemsSection
          }
          extraOptions={[
            {
              title: 'Settings',
              urlSnippet: 'settings',
              icon: <SettingsIcon />,
            },
          ]}
          baseUrl={`/manage/${params.communityId}`}
        />
      </div>

      <div className="flex h-full min-h-0 flex-col gap-y-3 font-inter max-tablet:hidden">
        <CommunityDisplay
          community={community}
          communityLoading={isLoading}
          session={session}
        />

        {!isLoading && !community?.discordServerId && <DiscordBotCTA />}

        {!isLoading && community && (
          <>
            {!pathname.includes('listing') && (
              <>
                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pb-8 pr-1">
                  <p className="mb-3 mt-5 text-p-1 font-semibold text-neutral-blue-400">
                    Community
                  </p>

                  {itemsSection.map((item) => (
                    <Link
                      key={item.title}
                      href={`/manage/${params.communityId}/${item.urlSnippet}`}
                      className="flex items-center gap-2 rounded-sm p-2 transition-all hover:bg-neutral-blue-600"
                    >
                      <div
                        className={cn(
                          'flex aspect-square items-center justify-center rounded-sm bg-neutral-blue-700 stroke-neutral-blue-200 p-1 [&>svg]:size-5',
                          {
                            'bg-shades-0 [&>svg]:stroke-neutral-blue-900':
                              pathname.endsWith(item.urlSnippet),
                          },
                        )}
                      >
                        {item.icon}
                      </div>
                      <p className="text-p-1 font-semibold">{item.title}</p>
                    </Link>
                  ))}

                  <div className="divide my-4 bg-neutral-blue-700"></div>

                  <Link
                    href={`/manage/${params.communityId}/settings`}
                    className="flex items-center gap-2 rounded-sm p-2 transition-all hover:bg-neutral-blue-600"
                  >
                    <div
                      className={cn(
                        'flex aspect-square items-center justify-center rounded-sm bg-neutral-blue-700 stroke-neutral-blue-200 p-1',
                        {
                          'bg-shades-0 [&>svg]:stroke-neutral-blue-900':
                            pathname.endsWith('settings'),
                        },
                      )}
                    >
                      <SettingsIcon className="size-5" />
                    </div>
                    <p className="text-p-1 font-semibold">Settings</p>
                  </Link>

                  <div className="divide my-4 bg-neutral-blue-700"></div>

                  {community?.championships?.length > 0 && (
                    <div className="mt-2 flex flex-col gap-3">
                      <p className="text-p-1 font-semibold text-neutral-blue-400">
                        Racing
                      </p>
                      <div className="flex flex-col gap-1">
                        {community?.championships.map((championship) => (
                          <Link
                            key={`${championship.name}-champ-${championship.id}`}
                            href={`/manage/${params.communityId}/racing/${championship.id}/results`}
                            className="flex items-center gap-2 rounded-sm p-2 transition-all hover:bg-neutral-blue-600"
                          >
                            <ImageWithFallback
                              height={64}
                              width={64}
                              className="size-7 rounded-sm"
                              src={championship.headerImgUrl}
                            />
                            <p className="truncate-1 text-p-1 font-semibold">
                              {championship.name}
                            </p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-gf-gradient mt-6 flex h-[52px] w-full shrink-0 items-center justify-center rounded-sm p-[2px]">
                    {/* TODO change the state of this button to "partner" if community is partnered */}
                    <Link
                      href={`/manage/${params.communityId}/partner`}
                      className="flex h-full w-full items-center justify-center bg-neutral-blue text-center text-p-1 font-semibold transition-all duration-150 hover:bg-neutral-blue-700"
                    >
                      Become a Partner
                    </Link>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  )
}
